/** @jsxImportSource @emotion/react */
'use memo'

import { useState } from 'react'
import styled from '@emotion/styled'
import { ORG_REGISTRATIONS_PER_PAGE } from '@sportninja/common/features/organization/registration/constants'
import type {
  OrgRegistration,
  OrgRegistrationTab,
} from '@sportninja/common/features/organization/registration/types'
import { buildOrgRegistrationListRows } from '@sportninja/common/features/organization/registration/utils/listRows'
import type { PagedList } from '@sportninja/common/hooks/usePagedList'
import ShowMoreFooter from 'src/components/ts-components/ShowMoreFooter'
import { useInfiniteScroll } from 'src/hooks/useInfiniteScroll'
import { useExpandedIds } from '../../hooks/useExpandedIds'
import OrgRegistrationEmptyState from '../OrgRegistrationEmptyState'
import OrgRegistrationTable from './OrgRegistrationTable'
import OrgRegistrationTableSkeleton from './OrgRegistrationTableSkeleton'

interface Props {
  list: PagedList<OrgRegistration>
  status: OrgRegistrationTab
  onOpen: (registration: OrgRegistration) => void
}

/** Rows shown while the first page loads. */
const COLD_LOAD_ROWS = 5

/** Rows standing in for the next page at the end of the table. */
const NEXT_PAGE_ROWS = 2

/**
 * The registrations as one sortable table — the same list, filters and
 * paging as the cards. Sorting orders the rows loaded so far; the next page
 * loads as the end of the table scrolls into view and takes its place in the
 * current order. A failed page stops the automatic loading and offers a retry.
 */
const OrgRegistrationListView = ({ list, status, onOpen }: Props) => {
  'use memo'

  // One moment for every row, so two rows never disagree about "today".
  const [now] = useState(() => new Date())
  const { expandedIds, toggle } = useExpandedIds()
  const rows = buildOrgRegistrationListRows(list.items, expandedIds, now)

  const canLoadMore = list.hasNextPage && !list.nextPageFailed
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: list.fetchNextPage,
    hasMore: canLoadMore,
    isLoading: list.isFetchingNextPage,
  })

  if (list.isLoading && list.items.length === 0) {
    return (
      <div aria-busy='true'>
        <OrgRegistrationTableSkeleton rows={COLD_LOAD_ROWS} withHeader />
      </div>
    )
  }

  if (list.items.length === 0) {
    return <OrgRegistrationEmptyState status={status} />
  }

  return (
    <Stack>
      <OrgRegistrationTable rows={rows} onToggle={toggle} onOpen={onOpen} />

      {canLoadMore && (
        <div ref={sentinelRef}>
          <OrgRegistrationTableSkeleton rows={NEXT_PAGE_ROWS} />
        </div>
      )}

      {(list.nextPageFailed || !list.hasNextPage) && (
        <ShowMoreFooter
          hasMore={false}
          onLoadMore={list.fetchNextPage}
          // Only once paging actually happened — a one-page list has no
          // "more" the reader was waiting for.
          showEndOfList={list.items.length > ORG_REGISTRATIONS_PER_PAGE}
          isError={list.nextPageFailed}
          onRetry={list.fetchNextPage}
        />
      )}
    </Stack>
  )
}

export default OrgRegistrationListView

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
